/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { DEFAULT_RADIUS } from '../../actions/search';

/**
 * Subset of the `search` criteria slice this derivation reads. The reducer is
 * untyped JS, so only the fields used to frame the map are declared here.
 */
export interface SearchFramingCriteria {
    lat?: number | string | null;
    lng?: number | string | null;
    map_center_lat?: number | null;
    map_center_lng?: number | null;
    latNe?: number | null;
    latSw?: number | null;
    lngNe?: number | null;
    lngSw?: number | null;
    radius?: number | string | null;
    what?: string | null;
}

/**
 * Subset of the `searchResults` slice this derivation reads. For generic text
 * queries the backend geocodes the text and returns the resolved location here
 * (not in the `search` criteria), so it acts as a fallback center/radius.
 */
export interface SearchFramingResultsLocation {
    lat?: number | string | null;
    lng?: number | string | null;
    radius?: number | string | null;
}

export interface SearchMapCenter {
    lat: number;
    lng: number;
}

export interface SearchMapFraming {
    /** Location to center on when there are no markers; null when nothing resolves. */
    searchCenter: SearchMapCenter | null;
    /** Radius (km) used to frame the map around searchCenter when there are no markers. */
    searchRadiusKm: number;
    /** True only when a text query resolved to no location (frame the whole US when empty). */
    showUsOverviewWhenEmpty: boolean;
}

/**
 * Average two bounding-box corner values, returning null when either corner is
 * not a finite number. Used to derive a center point from only the box corners.
 */
function averageCorners(near: unknown, far: unknown): number | null {
    if (typeof near !== 'number' || typeof far !== 'number') {
        return null;
    }
    // Average the two coordinates to get the midpoint between the corners.
    return (near + far) / 2;
}

/**
 * Pure derivation of the search map's framing inputs from the search criteria
 * and the search-results (geocoded) state. Has no Mapbox, DOM, or Redux
 * dependencies so it can be unit tested in isolation and reused by the map's
 * fit orchestration.
 *
 * @param searchState - The `search` criteria slice (subset).
 * @param searchResultsState - The `searchResults` slice (subset).
 * @returns The derived `searchCenter`, `searchRadiusKm`, and empty-state flag.
 */
export function deriveSearchMapFraming(
    searchState: SearchFramingCriteria,
    searchResultsState: SearchFramingResultsLocation
): SearchMapFraming {
    // Prefer explicit search criteria, then the map center, then the geocoded
    // response location, then the average of any bounding-box corners.
    // Number('' | null) is 0 and Number(undefined) is NaN, so missing values
    // are falsy and fall through the `||` chain.
    const lat =
        Number(searchState.lat) || // legacy redux value can be a number or string
        searchState.map_center_lat ||
        Number(searchResultsState.lat) || // geocoded location from the response
        averageCorners(searchState.latNe, searchState.latSw);
    const lng =
        Number(searchState.lng) || // legacy redux value can be a number or string
        searchState.map_center_lng ||
        Number(searchResultsState.lng) || // geocoded location from the response
        averageCorners(searchState.lngNe, searchState.lngSw);

    const searchCenter: SearchMapCenter | null =
        typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null;

    // The radius (km) the search covers. It is null in some flows
    // (geolocation/geoip and "Search this area"), so fall back to the response
    // radius (generic queries) then the default.
    const searchRadiusKm =
        Number(searchState.radius) || Number(searchResultsState.radius) || DEFAULT_RADIUS;

    // A text query resolved to no location when neither the user-supplied
    // criteria nor the geocoded response returned a center. When such a query
    // also returns no results, the map frames the whole US ("searched
    // everywhere, found nothing") instead of snapping back to the user's
    // location. Location-anchored and "search this area" flows keep their
    // existing recenter behavior.
    const hasResolvedSearchLocation =
        Boolean(Number(searchState.lat)) || Boolean(Number(searchResultsState.lat));
    const hasQueryText =
        typeof searchState.what === 'string' && searchState.what.trim().length > 0;
    const showUsOverviewWhenEmpty = hasQueryText && !hasResolvedSearchLocation;

    return { searchCenter, searchRadiusKm, showUsOverviewWhenEmpty };
}

// cSpell:ignore geoip
